// -----------------------------------------------------------------------------
// reportQuery.ts
//
// The client-side model + pure logic for the ad-hoc report builder (the
// ServiceNow-style "pick a table, choose fields, add a WHERE clause, join other
// tables" experience). Types mirror the BFF contract in CSM.Shared/DataQuery.
//
// Everything here is framework-free and unit-tested (see reportQuery.test.ts):
// SQL-type categorisation, the type-aware operator lists, value coercion, and
// the builder-state -> request serializer. The React page (ReportPage.tsx) owns
// only UI state and calls buildQueryRequest() when the user clicks Execute.
// -----------------------------------------------------------------------------

import {
  DatePresetId,
  dateBoundsFor,
  isRelativePreset,
  normalizeDatePreset,
  resolveDatePreset,
} from "./relativeDates";

/** Operator names — must match CSM.Shared.Enums.OperandType (serialized as strings). */
export type OperandType =
  | "IsLessThan"
  | "IsLessThanOrEqualTo"
  | "Equals"
  | "IsNotEqualTo"
  | "IsMoreThanOrEqualTo"
  | "IsMoreThan"
  | "StartsWith"
  | "EndsWith"
  | "Contains"
  | "DoesNotContain";

export type GroupOperand = "And" | "Or";
export type JoinKind = "Inner" | "Left";

/** How a join condition converts its two key columns before comparing them. Must
 *  match CSM.Shared.Enums.JoinCast (serialized as the member name).
 *  - "None"   compare as stored;
 *  - "Text"   CAST both sides to NVARCHAR;
 *  - "Number" TRY_CONVERT both sides to DECIMAL — this is what makes a text key
 *             joinable to a numeric one without the query failing on the first
 *             row whose text isn't a number. */
export type JoinCast = "None" | "Text" | "Number";

/** Per-field roll-up in an aggregate query. Must match CSM.Shared.Enums.FieldAggregate
 *  (serialized as the member name). "None" ⇒ a plain, non-aggregated column. */
export type FieldAggregate = "None" | "Sum" | "Avg" | "Min" | "Max" | "Count";

/** One column of a table, as returned by GET api/report/tables/{table}/columns. */
export interface ColumnInfo {
  name: string;
  sqlType: string;
}

// ---- Wire contract (POST api/report/execute) --------------------------------

export interface ReportFilter {
  field: string;
  table?: string;
  operator: OperandType;
  value: unknown;
  groupOperand?: GroupOperand;
}

/** One key comparison in a join's ON clause. Mirrors CSM.Shared.DataQuery.ReportJoinCondition. */
export interface ReportJoinCondition {
  leftField: string;
  rightField: string;
  cast: JoinCast;
}

export interface ReportJoin {
  table: string;
  leftTable?: string;
  /** The ON clause — one or more conditions, ANDed, so composite keys work. */
  conditions: ReportJoinCondition[];
  /** The first condition's key pair, repeated. Only there so a BFF deployed before
   *  multi-column joins existed still runs the join instead of rejecting it. */
  leftField: string;
  rightField: string;
  type: JoinKind;
  /** Legacy per-join display columns, derived from `select`. Ignored by a BFF that
   *  understands `select`; used by one that doesn't. */
  fields: string[];
}

/** One output column, qualified by its table. Mirrors CSM.Shared.DataQuery.ReportField. */
export interface ReportField {
  field: string;
  table?: string;
}

/** One GROUP BY field. Mirrors ReportFilter's table+field so the column stays
 *  unambiguous across joins. Matches CSM.Shared.DataQuery.ReportGroupBy. */
export interface ReportGroupBy {
  field: string;
  table?: string;
}

/** One summarized (aggregated) output column. Mirrors CSM.Shared.DataQuery.ReportMeasure. */
export interface ReportMeasure {
  field: string;
  table?: string;
  aggregate: FieldAggregate;
}

export interface ReportQueryRequest {
  table: string;
  /** Primary-table columns. Legacy fallback for a BFF that ignores `select`. */
  fields: string[];
  /** The output columns in display order across every in-play table. This is what
   *  lets a joined table's column be the report's first column. */
  select: ReportField[];
  filters: ReportFilter[];
  joins: ReportJoin[];
  groupBy: ReportGroupBy[];
  measures: ReportMeasure[];
  top?: number;
}

// ---- Field type categorisation ----------------------------------------------

export type FieldCategory = "numeric" | "datetime" | "bool" | "string";

const NUMERIC_TYPES = new Set([
  "bigint", "int", "smallint", "tinyint", "decimal", "numeric", "float", "real", "money", "smallmoney",
]);
const DATETIME_TYPES = new Set([
  "date", "datetime", "datetime2", "smalldatetime", "datetimeoffset", "time",
]);

/** Map a SQL Server DATA_TYPE to a builder category that drives operators + input. */
export function categorizeSqlType(sqlType: string | null | undefined): FieldCategory {
  const t = (sqlType ?? "").trim().toLowerCase();
  if (t === "bit") return "bool";
  if (NUMERIC_TYPES.has(t)) return "numeric";
  if (DATETIME_TYPES.has(t)) return "datetime";
  return "string";
}

export interface OperatorOption {
  value: OperandType;
  /** Friendly, ServiceNow-style label shown in the dropdown. */
  label: string;
}

const OP: Record<OperandType, string> = {
  Equals: "is",
  IsNotEqualTo: "is not",
  Contains: "contains",
  DoesNotContain: "does not contain",
  StartsWith: "starts with",
  EndsWith: "ends with",
  IsLessThan: "less than",
  IsLessThanOrEqualTo: "at most",
  IsMoreThan: "greater than",
  IsMoreThanOrEqualTo: "at least",
};

const opt = (value: OperandType): OperatorOption => ({ value, label: OP[value] });

const STRING_OPS: OperatorOption[] = [
  opt("Equals"), opt("IsNotEqualTo"), opt("Contains"), opt("DoesNotContain"), opt("StartsWith"), opt("EndsWith"),
];
const COMPARABLE_OPS: OperatorOption[] = [
  opt("Equals"), opt("IsNotEqualTo"), opt("IsLessThan"), opt("IsLessThanOrEqualTo"), opt("IsMoreThan"), opt("IsMoreThanOrEqualTo"),
];
const BOOL_OPS: OperatorOption[] = [opt("Equals"), opt("IsNotEqualTo")];

/** The operators that make sense for a field of the given category. */
export function operatorsFor(category: FieldCategory): OperatorOption[] {
  switch (category) {
    case "string": return STRING_OPS;
    case "numeric":
    case "datetime": return COMPARABLE_OPS;
    case "bool": return BOOL_OPS;
    default: return STRING_OPS;
  }
}

/** Which value editor the UI should render for a field category. */
export function valueInputFor(category: FieldCategory): "number" | "date" | "bool" | "text" {
  switch (category) {
    case "numeric": return "number";
    case "datetime": return "date";
    case "bool": return "bool";
    default: return "text";
  }
}

/** Equality operators are the only ones meaningful against an empty/NULL value. */
export function isEqualityOperator(operator: OperandType): boolean {
  return operator === "Equals" || operator === "IsNotEqualTo";
}

// ---- Builder state (owned by the page, consumed by the serializer) ----------

export interface BuilderCondition {
  id: string;
  table: string;
  field: string;
  category: FieldCategory;
  operator: OperandType;
  value: unknown;
  group: GroupOperand;
  /** Date conditions only: where the value comes from. Absent/"Specific" ⇒ the
   *  absolute date in `value` (the only behaviour before presets existed, and so
   *  what every report saved until then means). Anything else is resolved from
   *  the clock at Execute time and `value` is ignored — see relativeDates.ts. */
  datePreset?: DatePresetId;
  /** The N for the "Last N days" / "Next N days" presets. Ignored by the rest. */
  dateOffset?: number;
}

/** One row of a join's "match on" list in the builder. */
export interface BuilderJoinCondition {
  id: string;
  leftField: string;
  rightField: string;
  cast: JoinCast;
}

export interface BuilderJoin {
  id: string;
  table: string;
  leftTable: string;
  type: JoinKind;
  /** One or more key comparisons, ANDed together. */
  conditions: BuilderJoinCondition[];
}

/** A display column, qualified by the table it comes from. The builder keeps ONE
 *  ordered list of these spanning the primary table and every joined table, so the
 *  user can arrange columns freely (a joined column may come first). */
export interface SelectedField {
  table: string;
  field: string;
}

/** One row in the Group by builder section. */
export interface BuilderGroupBy {
  id: string;
  table: string;
  field: string;
}

/** Key a per-field aggregate to its (table, field) pair. Uses a unit-separator so
 *  a column name containing punctuation can never collide with the table name. */
const AGG_SEP = "";
export const fieldKey = (table: string, field: string): string => `${table}${AGG_SEP}${field}`;

export interface BuilderState {
  table: string;
  /** Every display column, in output order, across the primary and joined tables. */
  select: SelectedField[];
  /** Per-field aggregate keyed by fieldKey(table, field). Absent/"None" ⇒ dimension/plain. */
  aggregates?: Record<string, FieldAggregate>;
  conditions: BuilderCondition[];
  joins: BuilderJoin[];
  groupBy: BuilderGroupBy[];
  top?: number;
}

export const sameField = (a: SelectedField, b: SelectedField): boolean =>
  a.table === b.table && a.field === b.field;

/**
 * Move the item at `from` to index `to`, shifting the rest — the primitive behind
 * both the drag-and-drop and the move-left/right buttons in the field list.
 * Out-of-range indices (or a no-op move) return the list unchanged.
 */
export function reorder<T>(list: T[], from: number, to: number): T[] {
  if (from === to) return list;
  if (from < 0 || from >= list.length) return list;
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * The cast to preselect for a join condition, from the two columns' categories.
 * Same category ⇒ no conversion. A number-ish column against text ⇒ "Number":
 * TRY_CONVERT matches "0000123" to 123 and, unlike SQL Server's own implicit
 * conversion, a non-numeric value just fails to match instead of failing the query.
 * Anything else mixed (a date against text, say) ⇒ compare as text.
 */
export function suggestJoinCast(left: FieldCategory, right: FieldCategory): JoinCast {
  if (left === right) return "None";
  const numberish = (c: FieldCategory) => c === "numeric" || c === "bool";
  const textish = (c: FieldCategory) => c === "string";
  if (numberish(left) && numberish(right)) return "Number";
  if (numberish(left) && textish(right)) return "Number";
  if (textish(left) && numberish(right)) return "Number";
  return "Text";
}

/** The join conditions that are actually usable (both key columns chosen). */
export function completeJoinConditions(join: BuilderJoin): BuilderJoinCondition[] {
  return (join.conditions ?? []).filter((c) => c.leftField && c.rightField);
}

/** A join reaches the server only with a table, something to attach to, and at
 *  least one complete key pair. */
export function isJoinComplete(join: BuilderJoin): boolean {
  return Boolean(join.table && join.leftTable) && completeJoinConditions(join).length > 0;
}

/**
 * Coerce a raw input value to the type its column expects. Returns null for an
 * "empty" value (blank text / unparseable number) so the caller can treat it as
 * IS NULL / drop the row.
 */
export function coerceValue(category: FieldCategory, value: unknown): unknown {
  if (category === "bool") {
    return value === true || value === "true";
  }
  const s = typeof value === "string" ? value.trim() : value;
  if (s === "" || s === null || s === undefined) return null;
  if (category === "numeric") {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return s;
}

/**
 * Turn builder UI state into the POST body. Silently drops incomplete rows so a
 * half-built condition or join never reaches the server:
 *  - joins without a table / attach-to / at least one complete key pair are dropped,
 *    as are the individual half-filled key rows within a kept join;
 *  - display columns belonging to a table that isn't in play (a join that was
 *    removed or is still incomplete) are dropped, and duplicates collapse;
 *  - conditions without a field are dropped;
 *  - conditions whose value is empty are kept only for equality operators
 *    (→ IS NULL / IS NOT NULL) and dropped otherwise;
 *  - a date condition on a relative preset ("today", "last 30 days", …) is
 *    resolved against `now` and expands into the one or two comparisons that
 *    bracket that period — a relative preset that can't be resolved (no day
 *    count typed) is dropped.
 *
 * `now` is a parameter so the resolution is deterministic under test; callers
 * pass nothing and get the current clock. Resolving *here*, at Execute time,
 * rather than when the condition is edited, is what makes a saved report
 * re-read the current date every time it is run.
 */
export function buildQueryRequest(state: BuilderState, now: Date = new Date()): ReportQueryRequest {
  const usableJoins = (state.joins ?? []).filter(isJoinComplete);

  // Display columns: the builder's single ordered list, minus anything whose table
  // isn't in the query, minus repeats (which would collapse into one column anyway).
  const inPlay = new Set<string>([state.table, ...usableJoins.map((j) => j.table)]);
  const select: SelectedField[] = [];
  const seen = new Set<string>();
  for (const f of state.select ?? []) {
    if (!f.field || !f.table || !inPlay.has(f.table)) continue;
    const key = fieldKey(f.table, f.field);
    if (seen.has(key)) continue;
    seen.add(key);
    select.push({ table: f.table, field: f.field });
  }
  const fieldsOf = (table: string) => select.filter((f) => f.table === table).map((f) => f.field);

  const joins: ReportJoin[] = usableJoins.map((j) => {
    const conditions = completeJoinConditions(j).map((c) => ({
      leftField: c.leftField,
      rightField: c.rightField,
      cast: c.cast ?? "None",
    }));
    return {
      table: j.table,
      leftTable: j.leftTable,
      conditions,
      // Compat with a BFF that predates multi-condition joins / `select`: it reads
      // these and ignores the rest, so the report still runs (on the first key pair).
      leftField: conditions[0].leftField,
      rightField: conditions[0].rightField,
      type: j.type,
      fields: fieldsOf(j.table),
    };
  });

  const filters: ReportFilter[] = [];
  for (const c of state.conditions ?? []) {
    if (!c.field || !c.table) continue;

    // A relative date ("today", "last month", …) is a *period*, not a value: a
    // datetime column carries a time, so "is today" has to be
    // `>= today AND < tomorrow` or it only matches rows stamped midnight.
    // Both bounds are ANDed. That is safe in the BFF's flat, parenthesis-free
    // WHERE clause (ReportSqlBuilder.Build) because AND binds tighter than OR in
    // T-SQL, so the pair still reads as one unit whatever its neighbours are —
    // which is also why relativeDates.ts offers no "is not within" (that needs
    // an OR, and it would leak into the neighbouring conditions).
    if (c.category === "datetime" && isRelativePreset(c.datePreset)) {
      const range = resolveDatePreset(c.datePreset, c.dateOffset, now);
      if (!range) continue;
      const bounds = dateBoundsFor(c.operator, range);
      bounds.forEach((b, i) =>
        filters.push({
          field: c.field,
          table: c.table,
          operator: b.operator,
          value: b.value,
          // The row's own AND/OR joins the first bound to whatever came before
          // it; the second bound is always ANDed onto the first.
          groupOperand: i === 0 ? c.group : "And",
        })
      );
      continue;
    }

    const value = coerceValue(c.category, c.value);
    if (value === null && !isEqualityOperator(c.operator)) continue;
    filters.push({
      field: c.field,
      table: c.table,
      operator: c.operator,
      value,
      groupOperand: c.group,
    });
  }

  // Group-by rows need both a table and a field; incomplete rows are dropped.
  const groupBy: ReportGroupBy[] = (state.groupBy ?? [])
    .filter((g) => g.field && g.table)
    .map((g) => ({ field: g.field, table: g.table }));

  // Measures: every displayed field (primary + joined) whose aggregate is set to
  // something other than "None", in display order. The server ignores plain display
  // fields in aggregate mode, so only these summarized columns carry through.
  const aggregates = state.aggregates ?? {};
  const measures: ReportMeasure[] = [];
  for (const f of select) {
    const aggregate = aggregates[fieldKey(f.table, f.field)];
    if (aggregate && aggregate !== "None") measures.push({ field: f.field, table: f.table, aggregate });
  }

  return {
    table: state.table,
    fields: fieldsOf(state.table),
    select,
    filters,
    joins,
    groupBy,
    measures,
    top: state.top,
  };
}

// ---- Saved reports ----------------------------------------------------------

/** A persisted report, as returned by GET/POST api/report/saved. Matches
 *  CSM.Shared.DataQuery.SavedReport. `definition` is the JSON below. */
export interface SavedReport {
  id: number;
  name: string;
  definition: string;
  /** Emails this report is shared with (within the owner's site). Populated only
   *  for reports the caller owns; empty for a shared-with recipient. */
  sharedWith: string[];
  ownerEmail?: string;
  isOwner: boolean;
  createdDate?: string;
  modifiedDate?: string;
}

/**
 * The full builder state serialized into SavedReport.definition, so opening a
 * saved report rebuilds the workspace exactly. Versioned so the shape can evolve.
 */
export interface ReportDefinition {
  version: 3;
  table: string;
  /** Display columns in output order, across the primary and joined tables.
   *  (v1/v2 stored a primary-only `fields: string[]` plus per-join `fields`.) */
  select: SelectedField[];
  /** Per-field aggregate keyed by fieldKey(table, field). */
  aggregates: Record<string, FieldAggregate>;
  /** A date condition stores its relative-date *preset* (`datePreset`/`dateOffset`),
   *  never a resolved date — that's the point: the report re-reads the clock on
   *  every Execute. Both fields are optional additions to the v3 shape, so a
   *  definition written before they existed reads back as "Specific" and an older
   *  build simply ignores them. */
  conditions: BuilderCondition[];
  joins: BuilderJoin[];
  groupBy: BuilderGroupBy[];
  /** Chart is fully derived from the query (dimensions → category, measures →
   *  series); only the presentation chart type is persisted. */
  chart?: {
    type: string;
  };
  /** Which workspace tab the report opens on. "builder" is the default; older
   *  reports stored only "table"/"chart". */
  view?: "builder" | "table" | "chart";
}

export function serializeReportDefinition(def: ReportDefinition): string {
  return JSON.stringify(def);
}

/** Marker prefixed to a base64-encoded definition so the BFF knows to decode it. */
export const DEFINITION_B64_PREFIX = "b64:";

/**
 * Encode a serialized definition for transport as opaque base64 (with a marker).
 * The definition is a serialized-JSON string full of quotes and query-like tokens
 * (table/field names, operators, values); sent raw, an upstream WAF mistakes it for
 * a SQL-injection attempt and blocks the save with a 403 (surfacing in the browser
 * as a CORS error). Base64 hides those patterns. The BFF decodes it back to the same
 * JSON string before storing, so the database and read path are unchanged.
 */
export function encodeDefinitionForTransport(json: string): string {
  // btoa only accepts Latin-1; encodeURIComponent + unescape first turns the string
  // into its UTF-8 byte sequence so the BFF's base64→UTF-8 decode round-trips exactly.
  return DEFINITION_B64_PREFIX + btoa(unescape(encodeURIComponent(json)));
}

const asCast = (value: unknown): JoinCast =>
  value === "Text" || value === "Number" ? value : "None";

/**
 * Read one stored join into the current shape. v1/v2 stored a single key pair
 * (`leftField`/`rightField`) and the joined table's display columns (`fields`);
 * both are folded into the current model — the pair becomes the join's first (only)
 * condition, and the columns are lifted into the definition-wide `select` order by
 * {@link migratedSelect}. Rows are re-keyed with deterministic ids (`j0`, `j0k1`, …)
 * so a saved id can never collide with one the page mints for a newly added row.
 */
function normalizeJoin(raw: any, index: number, primaryTable: string): BuilderJoin {
  const id = `j${index}`;
  const rawConditions: any[] = Array.isArray(raw?.conditions) ? raw.conditions : [];
  const conditions: BuilderJoinCondition[] = rawConditions
    .filter((c) => c && c.leftField && c.rightField)
    .map((c, i) => ({
      id: `${id}k${i}`,
      leftField: String(c.leftField),
      rightField: String(c.rightField),
      cast: asCast(c.cast),
    }));

  // v1/v2 (or a join saved mid-edit with only the legacy pair filled in).
  if (conditions.length === 0 && raw?.leftField && raw?.rightField) {
    conditions.push({
      id: `${id}k0`,
      leftField: String(raw.leftField),
      rightField: String(raw.rightField),
      cast: "None",
    });
  }

  return {
    id,
    table: String(raw?.table ?? ""),
    leftTable: String(raw?.leftTable || primaryTable),
    type: raw?.type === "Left" ? "Left" : "Inner",
    conditions,
  };
}

/** v1/v2 display columns → the ordered, table-qualified `select` list: the primary
 *  table's columns first, then each join's, which is exactly the column order those
 *  reports produced before ordering was configurable. */
function migratedSelect(parsed: any): SelectedField[] {
  const select: SelectedField[] = [];
  const primary = String(parsed.table);
  for (const f of Array.isArray(parsed.fields) ? parsed.fields : []) {
    if (f) select.push({ table: primary, field: String(f) });
  }
  for (const j of Array.isArray(parsed.joins) ? parsed.joins : []) {
    if (!j?.table) continue;
    for (const f of Array.isArray(j.fields) ? j.fields : []) {
      if (f) select.push({ table: String(j.table), field: String(f) });
    }
  }
  return select;
}

/** Parse a stored definition. Returns null (rather than throwing) on anything
 *  malformed or from an unknown version, so a bad row can't break the page. */
export function parseReportDefinition(json: string | null | undefined): ReportDefinition | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed.version !== 1 && parsed.version !== 2 && parsed.version !== 3) ||
      typeof parsed.table !== "string"
    ) {
      return null;
    }
    // v1 had no per-field aggregates and carried a chart.aggregation; both are
    // dropped on upgrade (a v1 report simply opens with no measures set).
    const aggregates =
      parsed.aggregates && typeof parsed.aggregates === "object" ? parsed.aggregates : {};
    const chart = parsed.chart ? { type: String(parsed.chart.type ?? "Column") } : undefined;

    const storedSelect: SelectedField[] = (Array.isArray(parsed.select) ? parsed.select : [])
      .filter((f: any) => f && f.field)
      .map((f: any) => ({ table: String(f.table ?? parsed.table), field: String(f.field) }));

    const rawJoins: any[] = Array.isArray(parsed.joins) ? parsed.joins : [];
    const rawConditions: any[] = Array.isArray(parsed.conditions) ? parsed.conditions : [];
    const rawGroupBy: any[] = Array.isArray(parsed.groupBy) ? parsed.groupBy : [];

    return {
      version: 3,
      table: parsed.table,
      select: storedSelect.length > 0 ? storedSelect : migratedSelect(parsed),
      aggregates,
      conditions: rawConditions.map((c, i) => ({
        ...c,
        id: `c${i}`,
        // An unrecognised preset (an older/newer app, or a hand-edited row) falls
        // back to the absolute date in `value` — the pre-preset meaning — rather
        // than resolving to nothing and quietly dropping the filter.
        ...(c?.datePreset === undefined ? {} : { datePreset: normalizeDatePreset(c.datePreset) }),
      })),
      joins: rawJoins.map((j, i) => normalizeJoin(j, i, parsed.table)),
      groupBy: rawGroupBy.map((g, i) => ({ ...g, id: `g${i}` })),
      chart,
      view: parsed.view === "chart" || parsed.view === "table" ? parsed.view : "builder",
    };
  } catch {
    return null;
  }
}
